import {Skeleton} from 'antd'
import {course_getAttendanceReport_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import {callApi} from 'modules/asyncCache/callApi'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {BiChevronDown, BiChevronUp, BiSearch} from 'react-icons/bi'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'

const AVATAR_COLORS = [
    'bg-indigo-500', 'bg-green-500', 'bg-red-400',
    'bg-yellow-500', 'bg-purple-500', 'bg-blue-500', 'bg-pink-500', 'bg-teal-500',
]

const getAvatarColor = (str = '') => {
    let hash = 0
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const AttendanceStatusDot = ({detail}) => {
    if (!detail) return <span className="text-color-400 text-xs">-</span>
    if (detail.status === 1) return (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs font-bold leading-none">✓</span>
    )
    return (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-500 text-xs font-bold leading-none">✗</span>
    )
}

export const CourseClassAttendancePanel = ({item}) => {
    const t = useTranslate()
    const course_class_id = getId(item)
    const classSubjects = useMemo(
        () => (item?.class_subjects ?? []).filter(cs => !!cs?.subject),
        [item]
    )
    const subject_stats = item?.subject_stats ?? []
    const [selectedSubjectId, setSelectedSubjectId] = useState(
        () => String(getId(classSubjects[0]?.subject) ?? '') || null
    )
    const [keyword, setKeyword] = useState('')
    const [rateFilter, setRateFilter] = useState(null)
    const [expandedRows, setExpandedRows] = useState(new Set())
    const [attendanceData, setAttendanceData] = useState({students: [], sessions: [], loading: false})

    React.useEffect(() => {
        setKeyword('')
        setRateFilter(null)
        setExpandedRows(new Set())
        if (!selectedSubjectId || !course_class_id) return
        setAttendanceData({students: [], sessions: [], loading: true})
        callApi({
            apiInfo: course_getAttendanceReport_Api,
            query: bindQueryParams([{id: course_class_id}, {subject_id: selectedSubjectId}]),
        }).then(res => {
            const d = res?.data
            setAttendanceData({students: d?.data ?? [], sessions: d?.attendances ?? [], loading: false})
        }).catch(() => {
            setAttendanceData(prev => ({...prev, loading: false}))
        })
    }, [course_class_id, selectedSubjectId])

    const selectedCs = useMemo(
        () => classSubjects.find(cs => String(getId(cs.subject)) === String(selectedSubjectId)),
        [classSubjects, selectedSubjectId]
    )
    const selectedStats = subject_stats.find(s => String(s.subject_id) === String(selectedSubjectId))
    const threshold = selectedCs?.passed_attendance_percentage ?? 0

    const allSessions = attendanceData.sessions
    const recentSessions = allSessions.slice(-2)
    const hasMore = allSessions.length > 2

    const toggleExpand = useCallback((sid) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            next.has(sid) ? next.delete(sid) : next.add(sid)
            return next
        })
    }, [])

    const eligibilityFilters = useMemo(() => {
        const base = [{value: null, label: t('all'), color: null}]
        if (threshold > 0) {
            base.push({value: 'eligible', label: `≥${threshold}%`, sublabel: t('eligible'), color: 'green'})
            base.push({value: 'not_eligible', label: `<${threshold}%`, sublabel: t('not yet eligible'), color: 'red'})
        }
        return base
    }, [threshold, t])

    const filteredStudents = useMemo(() => {
        let list = attendanceData.students
        if (keyword) {
            const kw = keyword.toLowerCase()
            list = list.filter(s =>
                (s.fullname || '').toLowerCase().includes(kw) ||
                (s.student_idcode || '').toLowerCase().includes(kw)
            )
        }
        if (rateFilter === 'eligible') {
            list = list.filter(s => (s.cc_percent != null ? Number(s.cc_percent) : 0) >= threshold)
        } else if (rateFilter === 'not_eligible') {
            list = list.filter(s => (s.cc_percent != null ? Number(s.cc_percent) : 0) < threshold)
        }
        return list
    }, [attendanceData.students, keyword, rateFilter, threshold])

    return (
        <div className="flex flex-col gap-4">
            {/* Subject pill selector */}
            {classSubjects.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    <span className="text-color-300 tracking-wider">{`${t('subjects')} :`}</span>
                    <div className="flex flex-wrap gap-1.5">
                        {classSubjects.map((cs) => {
                            const sid = String(getId(cs.subject))
                            const isActive = sid === String(selectedSubjectId)
                            const pct = cs.passed_attendance_percentage
                            return (
                                <button
                                    key={sid}
                                    onClick={() => setSelectedSubjectId(sid)}
                                    className={classNames(
                                        'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors select-none',
                                        isActive
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'border-color-200 text-color-200 hover:border-indigo-400 hover:text-indigo-500'
                                    )}>
                                    <span>{getTitle(cs.subject)}</span>
                                    {pct > 0 && (
                                        <span className={classNames('text-2xs font-normal', isActive ? 'text-indigo-200' : 'text-color-400')}>
                                            {`≥${pct}%`}
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Stats cards */}
            {selectedStats && (
                <div className="flex gap-2">
                    <div className="flex-1 flex flex-col items-center p-3 bg-blue-50 rounded-xl text-center">
                        <span className="text-xl font-bold text-blue-600">{selectedStats.sessions_done ?? 0}</span>
                        <span className="text-xs text-color-400 mt-1">{t('sessions')}</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center p-3 bg-green-50 rounded-xl text-center">
                        <span className="text-xl font-bold text-green-600">{selectedStats.sessions_remaining ?? 0}</span>
                        <span className="text-xs text-color-400 mt-1">{t('sessions remaining')}</span>
                    </div>
                    <div className="flex-1 flex flex-col items-center p-3 bg-yellow-50 rounded-xl text-center">
                        <span className="text-xl font-bold text-yellow-600">
                            {selectedStats.avg_attendance_percentage != null ? `${selectedStats.avg_attendance_percentage}%` : '-'}
                        </span>
                        <span className="text-xs text-color-400 mt-1">{t('average rate')}</span>
                    </div>
                </div>
            )}

            {selectedSubjectId ? (
                <div className="flex flex-col gap-3">
                    {/* Eligibility filter pills */}
                    {threshold > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {eligibilityFilters.map((f, i) => {
                                const isActive = rateFilter === f.value
                                let cls = isActive
                                    ? f.color === 'green' ? 'bg-green-600 border-green-600 text-white'
                                        : f.color === 'red' ? 'bg-red-500 border-red-500 text-white'
                                            : 'bg-indigo-600 border-indigo-600 text-white'
                                    : f.color === 'green' ? 'border-color-200 text-green-600 hover:border-green-500'
                                        : f.color === 'red' ? 'border-color-200 text-red-500 hover:border-red-400'
                                            : 'border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                                return (
                                    <button
                                        key={i}
                                        onClick={() => setRateFilter(isActive ? null : f.value)}
                                        className={classNames('px-3 py-1 text-xs font-medium rounded-full border transition-colors select-none', cls)}>
                                        {f.label}
                                        {f.sublabel && <span className="ml-1 opacity-75">{`· ${f.sublabel}`}</span>}
                                    </button>
                                )
                            })}
                            <span className="text-xs text-green-600 font-medium italic self-center">
                                {`* ${t('passed attendance percentage')} : ${threshold}%`}
                            </span>
                        </div>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <BiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-color-400 pointer-events-none"/>
                        <input
                            type="text"
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            placeholder={t('search by name or student code')}
                            className="w-full pl-7 pr-6 py-2 text-xs border border-color-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 background text-color-100 placeholder-color-400 transition-colors"
                        />
                        {keyword && (
                            <button
                                onClick={() => setKeyword('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-xs transition-colors">
                                ×
                            </button>
                        )}
                    </div>

                    {attendanceData.loading ? (
                        <div className="flex flex-col gap-2 pt-1">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <Skeleton.Avatar active size={28}/>
                                    <Skeleton.Input active size="small" style={{flex: 1, height: 14}}/>
                                    <Skeleton.Input active size="small" style={{width: 28, height: 20}}/>
                                    <Skeleton.Input active size="small" style={{width: 28, height: 20}}/>
                                </div>
                            ))}
                        </div>
                    ) : filteredStudents.length === 0 ? (
                        <EmptyHolder title={null}/>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="background-100 border-b border-color-100">
                                        <th className="px-2 py-2 text-left text-xs font-semibold text-color-300 uppercase tracking-wider">{t('student')}</th>
                                        {recentSessions.map((s, i) => (
                                            <th key={s.id} className="px-2 py-2 text-center text-xs font-semibold text-color-300 uppercase whitespace-no-wrap">
                                                {s.title || `#${i + 1}`}
                                            </th>
                                        ))}
                                        <th className="px-2 py-2 text-center text-xs font-semibold text-color-300 uppercase">{t('attendance rate')}</th>
                                        {hasMore && <th className="px-2 py-2 w-8"/>}
                                    </tr>
                                </thead>
                                <tbody className="background divide-y divide-color-50">
                                    {filteredStudents.map((student) => {
                                        const sid = String(student.id)
                                        const studentAttendances = student.attendance_data ?? []
                                        const isExpanded = expandedRows.has(sid)
                                        const pct = student.cc_percent != null ? Number(student.cc_percent) : 0
                                        const name = student.fullname || '-'
                                        const initial = name.charAt(0).toUpperCase()
                                        let pctColor = 'text-color-300'
                                        let barColor = 'bg-gray-300'
                                        if (threshold > 0) {
                                            if (pct >= threshold) { pctColor = 'text-green-600'; barColor = 'bg-green-500' }
                                            else if (pct > 0) { pctColor = 'text-red-500'; barColor = 'bg-red-400' }
                                        } else {
                                            if (pct >= 75) { pctColor = 'text-green-600'; barColor = 'bg-green-500' }
                                            else if (pct >= 50) { pctColor = 'text-blue-500'; barColor = 'bg-blue-500' }
                                            else if (pct > 0) { pctColor = 'text-red-500'; barColor = 'bg-red-400' }
                                        }
                                        return (
                                            <React.Fragment key={sid}>
                                                <tr className="border-b border-color-50 hover:bg-gray-50 transition-colors">
                                                    <td className="px-2 py-2">
                                                        <div className="flex flex-col gap-0.5">
                                                            <div className="flex items-center gap-2">
                                                                <div className={classNames('w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0', getAvatarColor(name))}>
                                                                    {initial}
                                                                </div>
                                                                <span className="text-sm font-semibold text-gray-700 truncate max-w-[100px]">{name}</span>
                                                            </div>
                                                            {student.student_idcode && (
                                                                <span className="text-2xs text-blue-500 ml-8 font-mono">{student.student_idcode}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    {recentSessions.map(s => {
                                                        const detail = studentAttendances.find(a => a.attendance_id === s.id)
                                                        return (
                                                            <td key={s.id} className="px-2 py-2 text-center">
                                                                <AttendanceStatusDot detail={detail}/>
                                                            </td>
                                                        )
                                                    })}
                                                    <td className="px-2 py-2">
                                                        <div className="flex items-center gap-1.5" style={{minWidth: 80}}>
                                                            <div className="flex-1 bg-gray-100 rounded-full" style={{height: 5}}>
                                                                <div
                                                                    className={classNames('rounded-full transition-all', barColor)}
                                                                    style={{width: `${Math.min(pct, 100)}%`, height: 5}}
                                                                />
                                                            </div>
                                                            <span className={classNames('text-xs font-bold tabular-nums flex-shrink-0', pctColor)} style={{minWidth: 34, textAlign: 'right'}}>{`${pct}%`}</span>
                                                        </div>
                                                    </td>
                                                    {hasMore && (
                                                        <td className="px-2 py-2 text-center">
                                                            <button
                                                                className="p-0.5 rounded hover:bg-gray-100 text-color-300 hover:text-color-100 transition-colors"
                                                                onClick={() => toggleExpand(sid)}>
                                                                {isExpanded ? <BiChevronUp size={14}/> : <BiChevronDown size={14}/>}
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-blue-50/20 border-b border-color-50">
                                                        <td colSpan={recentSessions.length + (hasMore ? 3 : 2)} className="px-3 py-2">
                                                            <div className="flex flex-wrap gap-2">
                                                                {studentAttendances.map((a, i) => (
                                                                    <div key={a.attendance_id} className={classNames(
                                                                        'flex flex-col items-center px-2 py-2 rounded-lg border text-xs gap-1 min-w-[52px] max-w-[72px]',
                                                                        a.status === 1 ? 'bg-green-50 border-green-200' :
                                                                        a.status === -2 ? 'bg-red-50 border-red-200' :
                                                                        (a.status === -1 || a.status === -3) ? 'bg-yellow-50 border-yellow-200' :
                                                                        'bg-gray-50 border-color-100'
                                                                    )}>
                                                                        <span className="text-color-400 text-center leading-tight truncate w-full">
                                                                            {a.title || `#${i + 1}`}
                                                                        </span>
                                                                        <AttendanceStatusDot detail={a}/>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <EmptyHolder title={null}/>
            )}
        </div>
    )
}
